import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Loader2, RotateCcw, X } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Settings → Newsletter → "Design" form.
 *
 * Edits the property's `newsletter_theme` — the per-property skin the
 * backend `newsletter_template.py` renders from. The `theme` prop is
 * the fully-resolved theme (`settings.newsletter_theme_effective`), so
 * every field always shows its effective value; `defaults` is
 * `DEFAULT_THEME` for the Reset button. `onChange(nextTheme)` hands the
 * whole object back to SettingsPage, which stores it on the draft and
 * sends it on Save like any other key.
 *
 * "Preview theme" posts the UNSAVED draft theme to
 * `/newsletter/theme-preview`, which renders it with the property's
 * most recent published stories and active sponsor ads, so colours and
 * fonts can be tuned without going through the Newsletter page.
 */

const SOCIAL_KEYS = [
  ["facebook", "Facebook"],
  ["instagram", "Instagram"],
  ["x", "X / Twitter"],
  ["youtube", "YouTube"],
  ["spotify", "Spotify"],
  ["apple_podcasts", "Apple Podcasts"],
];

const inputCls =
  "w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]";
const labelCls =
  "text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && (
        <p className="text-[11px] text-[var(--ink-muted)] mt-1">{hint}</p>
      )}
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder, hint, testId }) {
  return (
    <Field label={label} hint={hint}>
      <input
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
      />
    </Field>
  );
}

function NumberInput({ label, value, onChange, min, max, hint, testId }) {
  return (
    <Field label={label} hint={hint}>
      <input
        data-testid={testId}
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className={inputCls}
      />
    </Field>
  );
}

function ColorInput({ label, value, onChange, testId }) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#000000";
  return (
    <Field label={label}>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="color"
          data-testid={testId ? `${testId}-picker` : undefined}
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 border border-[var(--border)] cursor-pointer p-0"
        />
        <input
          type="text"
          data-testid={testId}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
        />
      </div>
    </Field>
  );
}

function Toggle({ label, checked, onChange, hint, testId }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span>
        <span className="text-sm text-[var(--ink)]">{label}</span>
        {hint && (
          <span className="block text-[11px] text-[var(--ink-muted)]">{hint}</span>
        )}
      </span>
    </label>
  );
}

function Group({ title, children }) {
  return (
    <div className="border border-[var(--border)] p-3 sm:p-4 space-y-3 bg-[var(--paper,#fafaf7)]">
      <h4 className="font-serif-display text-sm text-[var(--ink)]">{title}</h4>
      {children}
    </div>
  );
}

export default function NewsletterThemeEditor({
  theme,
  defaults,
  fontOptions = [],
  onChange,
}) {
  const t = theme || {};
  const [previewHtml, setPreviewHtml] = useState(null);

  const set = (key, value) => onChange({ ...t, [key]: value });
  const setNested = (group, key, value) =>
    onChange({ ...t, [group]: { ...(t[group] || {}), [key]: value } });

  const previewMut = useMutation({
    mutationFn: () => api.newsletterThemePreview({ theme: t }),
    onSuccess: (r) => setPreviewHtml(r.html),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Theme preview failed")),
  });

  const resetAll = () => {
    if (!defaults) return;
    if (!window.confirm(tr("Reset the newsletter design to the built-in defaults? Unsaved edits are lost."))) return;
    // Keep the two property-derived values so the form doesn't go blank.
    onChange({
      ...defaults,
      logo_link: t.logo_link || "",
      footer_name: t.footer_name || "",
    });
  };

  const fonts = fontOptions.length ? fontOptions : ["Inter", "Poppins", "Oswald", "Helvetica", "Georgia"];
  const cta = t.cta || {};
  const adv = t.advertise || {};
  const social = t.social_links || {};

  return (
    <div className="pt-2 border-t border-[var(--border)] mt-2 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h3 className="font-serif-display text-base text-[var(--ink)]">{tr("Newsletter design")}</h3>
          <p className="text-[11px] text-[var(--ink-muted)] mt-0.5">{tr("Per-property skin for the newsletter HTML. Preview renders the unsaved form with this property's latest published stories; remember to Save settings afterwards.")}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            data-testid="newsletter-theme-reset"
            onClick={resetAll}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--paper)]"
          >
            <RotateCcw className="w-3.5 h-3.5" />{" "}{tr("Reset to defaults")}</button>
          <button
            type="button"
            data-testid="newsletter-theme-preview"
            onClick={() => previewMut.mutate()}
            disabled={previewMut.isPending}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-50"
          >
            {previewMut.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Eye className="w-3.5 h-3.5" />
            )}{tr("Preview theme")}</button>
        </div>
      </div>

      <Group title={tr("Masthead")}>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <TextInput label={tr("Logo image URL")} value={t.logo_url} onChange={(v) => set("logo_url", v)}
              placeholder="https://…/logo.png" hint={tr("Blank shows the property name as text.")} testId="nt-logo-url" />
          </div>
          <NumberInput label={tr("Logo width (px)")} value={t.logo_width} onChange={(v) => set("logo_width", v)} min={80} max={540} testId="nt-logo-width" />
        </div>
        <TextInput label={tr("Logo link")} value={t.logo_link} onChange={(v) => set("logo_link", v)} placeholder="https://…" testId="nt-logo-link" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextInput label={tr("Heading")} value={t.heading_text} onChange={(v) => set("heading_text", v)} placeholder={tr("Your Baja Update!")} hint={tr("Blank hides the heading.")} testId="nt-heading" />
          <TextInput label={tr("Tagline")} value={t.tagline} onChange={(v) => set("tagline", v)} placeholder={tr("What's happening in Baja — straight to your inbox")} hint={tr("Blank hides the tagline.")} testId="nt-tagline" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={tr("Heading alignment")}>
            <select data-testid="nt-heading-align" value={t.heading_align || "center"} onChange={(e) => set("heading_align", e.target.value)} className={inputCls}>
              <option value="center">{tr("Centered")}</option>
              <option value="left">{tr("Left")}</option>
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Toggle label={tr("Show date band")} checked={t.show_date_band} onChange={(v) => set("show_date_band", v)} hint={tr("Grey band with the issue date under the tagline.")} testId="nt-date-band" />
          <Toggle label={tr("Show AI editorial intro")} checked={t.show_intro} onChange={(v) => set("show_intro", v)} hint={tr("Short intro paragraph under the date. Off = compose skips generating it.")} testId="nt-show-intro" />
        </div>
      </Group>

      <Group title={tr("Type & colour")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={tr("Body font")}>
            <select data-testid="nt-font" value={t.font_family || "Inter"} onChange={(e) => set("font_family", e.target.value)} className={inputCls}>
              {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label={tr("Heading font")} hint={tr("Same as body when blank.")}>
            <select data-testid="nt-heading-font" value={t.heading_font_family || ""} onChange={(e) => set("heading_font_family", e.target.value)} className={inputCls}>
              <option value="">{tr("Same as body")}</option>
              {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <ColorInput label={tr("Brand (heading, CTA card)")} value={t.brand_color} onChange={(v) => set("brand_color", v)} testId="nt-brand-color" />
          <ColorInput label={tr("Buttons")} value={t.button_color} onChange={(v) => set("button_color", v)} testId="nt-button-color" />
          <ColorInput label={tr("Bands & rules")} value={t.band_color} onChange={(v) => set("band_color", v)} testId="nt-band-color" />
          <ColorInput label={tr("Page background")} value={t.page_bg} onChange={(v) => set("page_bg", v)} testId="nt-page-bg" />
          <ColorInput label={tr("Card background")} value={t.card_bg} onChange={(v) => set("card_bg", v)} testId="nt-card-bg" />
          <ColorInput label={tr("Headlines")} value={t.headline_color} onChange={(v) => set("headline_color", v)} testId="nt-headline-color" />
          <ColorInput label={tr("Body text")} value={t.text_color} onChange={(v) => set("text_color", v)} testId="nt-text-color" />
        </div>
      </Group>

      <Group title={tr("Stories")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Toggle label={tr("Lead story")} checked={t.lead_story} onChange={(v) => set("lead_story", v)} hint={tr("First story gets a full-width image and larger headline.")} testId="nt-lead-story" />
          <Toggle label={tr("Show bylines")} checked={t.show_bylines} onChange={(v) => set("show_bylines", v)} hint={tr("“2026-09-11 • Author” under each headline.")} testId="nt-bylines" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <NumberInput label={tr("Thumbnail width (px)")} value={t.thumb_width} onChange={(v) => set("thumb_width", v)} min={70} max={240} hint={tr("70 = compact rows, 160 = larger photos.")} testId="nt-thumb-width" />
          <NumberInput label={tr("Stories between ads")} value={t.stories_per_group} onChange={(v) => set("stories_per_group", v)} min={1} max={8} testId="nt-per-group" />
          <NumberInput label={tr("Excerpt length (chars)")} value={t.excerpt_chars} onChange={(v) => set("excerpt_chars", v)} min={80} max={600} testId="nt-excerpt-chars" />
        </div>
      </Group>

      <Group title={tr("Sponsors")}>
        <TextInput label={tr("Sponsor label")} value={t.sponsor_label} onChange={(v) => set("sponsor_label", v)} placeholder={tr("TODAY'S NEWSLETTER SPONSOR")} hint={tr("Shown above the first ad. Blank hides the label.")} testId="nt-sponsor-label" />
        <Toggle label={tr("First ad directly under the lead story")} checked={t.sponsor_slot_after_lead} onChange={(v) => set("sponsor_slot_after_lead", v)} hint={tr("Off = all ads run edge-to-edge between story groups.")} testId="nt-sponsor-slot" />
      </Group>

      <Group title={tr("Events / call-to-action card")}>
        <Toggle label={tr("Enabled")} checked={cta.enabled} onChange={(v) => setNested("cta", "enabled", v)} hint={tr("Needs a URL to render.")} testId="nt-cta-enabled" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextInput label={tr("Title")} value={cta.title} onChange={(v) => setNested("cta", "title", v)} testId="nt-cta-title" />
          <TextInput label={tr("Subtitle")} value={cta.subtitle} onChange={(v) => setNested("cta", "subtitle", v)} testId="nt-cta-subtitle" />
          <TextInput label={tr("Button label")} value={cta.button_label} onChange={(v) => setNested("cta", "button_label", v)} testId="nt-cta-label" />
          <TextInput label={tr("URL")} value={cta.url} onChange={(v) => setNested("cta", "url", v)} placeholder="https://…/events/" testId="nt-cta-url" />
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <ColorInput label={tr("Title")} value={cta.title_color} onChange={(v) => setNested("cta", "title_color", v)} testId="nt-cta-title-color" />
          <ColorInput label={tr("Text")} value={cta.text_color} onChange={(v) => setNested("cta", "text_color", v)} testId="nt-cta-text-color" />
          <ColorInput label={tr("Button")} value={cta.button_bg} onChange={(v) => setNested("cta", "button_bg", v)} testId="nt-cta-button-bg" />
          <ColorInput label={tr("Button text")} value={cta.button_text_color} onChange={(v) => setNested("cta", "button_text_color", v)} testId="nt-cta-button-text" />
        </div>
      </Group>

      <Group title={tr("Advertise-with-us card")}>
        <Toggle label={tr("Enabled")} checked={adv.enabled} onChange={(v) => setNested("advertise", "enabled", v)} hint={tr("Needs a URL to render. Button uses the Buttons colour.")} testId="nt-adv-enabled" />
        <div className="grid sm:grid-cols-3 gap-3">
          <TextInput label={tr("Text")} value={adv.text} onChange={(v) => setNested("advertise", "text", v)} testId="nt-adv-text" />
          <TextInput label={tr("Button label")} value={adv.button_label} onChange={(v) => setNested("advertise", "button_label", v)} testId="nt-adv-label" />
          <TextInput label={tr("URL")} value={adv.url} onChange={(v) => setNested("advertise", "url", v)} placeholder="https://…/contact/" testId="nt-adv-url" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <ColorInput label={tr("Card background")} value={adv.bg} onChange={(v) => setNested("advertise", "bg", v)} testId="nt-adv-bg" />
        </div>
      </Group>

      <Group title={tr("Footer & social")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={tr("Footer style")} hint={tr("Check MailerLite → Account settings → Default settings: if a footer is set there, MailerLite appends it to every send and Minimal avoids showing name, address and blurb twice.")}>
            <select data-testid="nt-footer-style" value={t.footer_style || "minimal"} onChange={(e) => set("footer_style", e.target.value)} className={inputCls}>
              <option value="minimal">{tr("Minimal — logo, social icons, unsubscribe")}</option>
              <option value="full">{tr("Full — name, address, blurb, unsubscribe")}</option>
            </select>
          </Field>
          <TextInput label={tr("Footer name")} value={t.footer_name} onChange={(v) => set("footer_name", v)} hint={tr("Also the logo's alt text and the no-logo fallback.")} testId="nt-footer-name" />
        </div>
        {(t.footer_style || "minimal") === "full" && (
          <TextInput label={tr("Footer blurb")} value={t.footer_blurb} onChange={(v) => set("footer_blurb", v)} hint={tr("Full footer only. Defaults to MailerLite's own wording.")} testId="nt-footer-blurb" />
        )}
        <p className="text-[11px] text-[var(--ink-muted)]">{tr("The unsubscribe link is in both styles — MailerLite's own footer doesn't carry one. The full footer also prints the physical address from the CAN-SPAM field above, which becomes required to send. Social icons only render for links that are filled in.")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {SOCIAL_KEYS.map(([k, label]) => (
            <TextInput key={k} label={label} value={social[k]} onChange={(v) => setNested("social_links", k, v)} placeholder="https://…" testId={`nt-social-${k}`} />
          ))}
        </div>
      </Group>

      {previewHtml !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6"
          onClick={() => setPreviewHtml(null)}
        >
          <div
            className="bg-white w-full max-w-4xl h-[92vh] flex flex-col border border-[var(--border)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)]">
              {trRich("<0>Theme preview — latest published stories</0>", null, [<span className="font-serif-display text-sm" />])}
              <button
                type="button"
                data-testid="newsletter-theme-preview-close"
                onClick={() => setPreviewHtml(null)}
                className="p-1 hover:bg-[var(--paper)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <iframe
              data-testid="newsletter-theme-preview-iframe"
              title={tr("Newsletter theme preview")}
              srcDoc={previewHtml}
              className="flex-1 w-full border-0 bg-[#F4F7FA]"
              sandbox=""
            />
          </div>
        </div>
      )}
    </div>
  );
}